import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
  UseQueryResult,
  UseMutationOptions,
} from 'react-query'
import { MembershipTypesApi } from '@trybeapp/sdk.customer'
import {
  ActionCopyMembershipTypeRequest,
  CreateSiteMembershipType,
  ListMembershipTypes,
  ListMembershipTypesRequest,
  ListSiteMembershipTypes,
  MembershipTypesApi as MembershipTypesApiTs,
  ResponseError,
  ShowMembershipType,
  ShowMembershipTypeOfferingResponse,
  UpdateMembershipType,
} from '@trybeapp/sdk'
import { useOrganisationId } from 'contexts/OrganisationConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { useSiteConfig } from 'hooks/UseSiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { toIsoDate } from 'utilities/DateUtils/dateUtils'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { LegacySdkResponseError } from 'api/api.types'

const api = new MembershipTypesApi()
const tsApi = new MembershipTypesApiTs(apiConfiguration)

export const useMembershipTypes = <TData = ListMembershipTypes>(
  params: ListMembershipTypesRequest = {},
  options: UseQueryOptions<ListMembershipTypes, ResponseError, TData> = {}
) => {
  return useQuery<ListMembershipTypes, ResponseError, TData>(
    ['membershipTypes', params],
    () => tsApi.listMembershipTypes(params),
    {
      refetchInterval: 600000, // every 10 minutes
      refetchOnWindowFocus: false,
      ...options,
    }
  )
}

export const useSetRevenueForMembershipType = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()
  return useMutation<
    ShowMembershipTypeOfferingResponse,
    ResponseError,
    { membershipTypeId: string; revenue_centre: string }
  >(
    (params) => {
      return tsApi.updateMembershipTypeRevenueCentre({
        membershipTypeId: params.membershipTypeId,
        siteId,
        updateMembershipTypeRevenueCentreRequest: {
          revenue_centre: params.revenue_centre,
        },
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipTypesRevenuesCentre')
      },
    }
  )
}

export const useRevenueForMembershipType = (
  membershipTypeId: string,
  options: UseQueryOptions<ShowMembershipTypeOfferingResponse, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ShowMembershipTypeOfferingResponse, ResponseError>(
    ['membershipTypesRevenuesCentre', { membershipTypeId, siteId }],
    () =>
      tsApi.getMembershipTypesRevenuesCentre({
        siteId: siteId,
        membershipTypeId: membershipTypeId,
      }),
    {
      enabled: !!membershipTypeId,
      ...options,
    }
  )
}

export const useSiteMembershipTypes = (
  options: UseQueryOptions<ListSiteMembershipTypes, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListSiteMembershipTypes, ResponseError>(
    ['membershipTypes', { siteId }],
    () => tsApi.listSiteMembershipTypes({ siteId }),
    options
  )
}

export const useListSingleSiteMembershipType = (
  siteId: string,
  membershipTypeId: string,
  options: UseQueryOptions<ShowMembershipType, ResponseError> = {}
) => {
  return useQuery<ShowMembershipType, ResponseError>(
    ['singleSitemembershipType', siteId, membershipTypeId],
    async () =>
      await tsApi.listSingleSiteMembershipType({
        siteId,
        membershipTypeId,
      }),
    {
      ...options,
    }
  )
}

export const useCreateSiteMembershipType = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    (createSiteMembershipType: CreateSiteMembershipType) =>
      tsApi.createSiteMembershipType({
        siteId,
        createSiteMembershipType: createSiteMembershipType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipTypes')
      },
    }
  )
}

export const useDeleteSiteMembershipType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ siteId, membershipTypeId }: { siteId: string; membershipTypeId: string }) =>
      tsApi.deleteSiteMembershipType({ siteId, membershipTypeId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipTypes')
      },
    }
  )
}

export const usePagedMembershipTypes = (params = {}, options = {}) => {
  const [page] = usePageQueryParam()

  return useMembershipTypes({ page, ...params }, options)
}

export const useInfiniteMembershipTypes = (params: ListMembershipTypesRequest = {}, options = {}) =>
  useInfiniteQuery(['membershipTypes', { params }], fetchMembershipTypes, {
    getNextPageParam: (lastPage, pages) => {
      return lastPage.nextPage
    },
    ...options,
  })

const fetchMembershipTypes = async ({ queryKey, pageParam: page = 1 }) => {
  const { params } = queryKey[1]
  const response = await tsApi.listMembershipTypes({ page, ...params })

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useMembershipType = (
  membershipTypeId: string,
  options: UseQueryOptions<ShowMembershipType['data'], ResponseError> = {}
) => {
  return useQuery<ShowMembershipType['data'], ResponseError>(
    ['membershipType', membershipTypeId],
    async () => (await tsApi.getMembershipType({ membershipTypeId })).data,
    {
      ...options,
    }
  )
}

export const useOrderedMembershipTypes = (
  params: any = {},
  options: UseQueryOptions = {}
): UseQueryResult<any> => {
  const { site } = useSiteConfig()
  const { brand_id: brandId } = site

  return useQuery(
    ['membershipTypes', params, brandId],
    () => api.getOrderedMembershipTypes(params, brandId),
    {
      refetchInterval: 600000, // every 10 minutes
      refetchOnWindowFocus: false,
      ...options,
    }
  )
}

export const useUpdateOrderedMembershipTypes = () => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, string[]>(
    (membershipTypeIds) =>
      api.updateMembershipTypeOrder({
        membership_type_ids: membershipTypeIds,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['membershipTypes'])
      },
    }
  )
}

export const useCreateMembershipType = (
  options: UseMutationOptions<any, LegacySdkResponseError, Record<string, unknown>> = {}
) => {
  const queryClient = useQueryClient()
  const organisationId = useOrganisationId()

  return useMutation<any, LegacySdkResponseError, Record<string, unknown>>(
    (membershipType) =>
      api.createMembershipType({
        createMembershipTypeRequest: {
          ...membershipType,
          organisation_id: organisationId,
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries('membershipTypes')
        if (options.onSuccess) {
          options.onSuccess(...args)
        }
      },
    }
  )
}

export const useCopyMembershipType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ membershipTypeId, copyMembershipType }: ActionCopyMembershipTypeRequest) => {
      return await tsApi.actionCopyMembershipType({ membershipTypeId, copyMembershipType })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipTypes')
      },
    }
  )
}

export const useUpdateMembershipType = (
  membershipTypeId: string,
  options: UseMutationOptions<any, LegacySdkResponseError, Record<string, any>> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, Record<string, any>>(
    async (membershipType) => {
      const values = { ...membershipType }

      if (typeof membershipType.minimum_start_date === 'object') {
        values.minimum_start_date = toIsoDate(membershipType.minimum_start_date)
      }

      return await api.updateMembershipType(membershipTypeId, {
        updateMembershipTypeRequest: values,
      })
    },
    {
      ...options,
      onMutate: async (newData) => {
        // Cancel any outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries(['membershipType', membershipTypeId])

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(['membershipType', membershipTypeId])

        // Optimistically update to the new value
        queryClient.setQueryData(['membershipType', membershipTypeId], (old: any) => ({
          ...old,
          ...newData,
        }))

        // Return a context object with the snapshotted value
        return { previousData }
      },
      onSuccess: (...args) => {
        queryClient.invalidateQueries('membershipTypes')
        queryClient.invalidateQueries(['membershipType', membershipTypeId])
        if (options.onSuccess) {
          options.onSuccess(...args)
        }
      },
    }
  )
}

export const useUpdateMembershipTypeUsingNewSdk = (membershipTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    async (updateMembershipType: UpdateMembershipType) => {
      return await tsApi.updateMembershipType({
        membershipTypeId,
        updateMembershipType,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipTypes')
        queryClient.invalidateQueries('membershipType')
      },
    }
  )
}

export const useDeleteMembershipType = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation((membershipTypeId) => api.archiveMembershipType(membershipTypeId), {
    onSuccess: () => {
      queryClient.invalidateQueries('membershipTypes')
    },
    ...options,
  })
}
